export function matchesFacets(facets, selected, except) {
  return Object.entries(selected).every(([key, values]) => key === except || !values.length || values.some(value => (facets[key] || []).includes(value)));
}

export function selectProducts(products, selected, sort = 'manual') {
  const result = products.filter(p => matchesFacets(p.facets, selected));
  if (sort === 'price-ascending') result.sort((a,b) => a.price-b.price);
  if (sort === 'price-descending') result.sort((a,b) => b.price-a.price);
  if (sort === 'title-ascending') result.sort((a,b) => a.title.localeCompare(b.title,'en'));
  if (sort === 'title-descending') result.sort((a,b) => b.title.localeCompare(a.title,'en'));
  return result;
}

const initialized = new WeakSet();
export function initializeCollections(scope = document) {
  const sections = [...scope.querySelectorAll('[data-faith-collection]')];
  if (scope.matches?.('[data-faith-collection]')) sections.unshift(scope);
  sections.forEach(section => {
    if (initialized.has(section)) return;
    initialized.add(section);
    const dialog = section.querySelector('.faith-facet-drawer');
    section.querySelector('[data-open-facets]')?.addEventListener('click', () => dialog.showModal());
    const grid = section.querySelector('[data-collection-grid]');
    const viewControls = [...section.querySelectorAll('[data-grid-mode]')];
    if (grid && viewControls.length) {
      const storageKey = `faith-grid-mode:${location.pathname}`;
      let saved = 'comfortable';
      try { saved = localStorage.getItem(storageKey) || saved; } catch {}
      const applyGridMode = mode => {
        const valid = viewControls.some(button => button.dataset.gridMode === mode) ? mode : 'comfortable';
        grid.dataset.gridMode = valid;
        viewControls.forEach(button => {
          const active = button.dataset.gridMode === valid;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        try { localStorage.setItem(storageKey, valid); } catch {}
      };
      viewControls.forEach(button => button.addEventListener('click', () => applyGridMode(button.dataset.gridMode)));
      applyGridMode(saved);
    }
    const forms = [...section.querySelectorAll('[data-facet-form]')];
    if (section.dataset.localFacets !== 'true') {
      forms.forEach(form => form.addEventListener('change', () => {
        if (form.dataset.facetMode === 'desktop') form.requestSubmit();
      }));
      return;
    }
    const controller = new AbortController();
    section.addEventListener('faith:dispose', () => controller.abort());
    const pagination = section.querySelector('[data-collection-pagination]');
    const chips = section.querySelector('[data-active-filters]');
    const error = section.querySelector('[data-collection-error]');
    const perPage = Number(section.dataset.perPage) || 24;
    let catalogPromise;
    const fromTemplate = template => [...(template?.content.querySelectorAll('[data-facet-item]') || [])].map(el => {
      let facets = {}; try { facets = JSON.parse(el.dataset.facets || '{}') || {}; } catch {}
      return {handle:el.dataset.handle,facets,price:Number(el.dataset.price),title:el.dataset.title,card:el.querySelector('[data-product-card]')};
    }).filter(item=>item.card);
    const catalog = () => catalogPromise ||= (async () => {
      const template = section.querySelector('[data-collection-catalog]');
      const items = Number(template?.dataset.currentPage) === 1 ? fromTemplate(template) : [];
      const pages = Number(template?.dataset.pages) || 1;
      for (let page = items.length ? 2 : 1; page <= pages; page++) {
        const url = new URL(section.dataset.collectionUrl, location.origin);
        url.searchParams.set('section_id', section.dataset.sectionId);
        url.searchParams.set('page',page);
        const response = await fetch(url,{credentials:'same-origin',signal:controller.signal});
        if (!response.ok) throw Error('Unable to load products. Please refresh and try again.');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const next = doc.querySelector('[data-collection-catalog]');
        if (!next) throw Error('Unable to load products. Please refresh and try again.');
        items.push(...fromTemplate(next));
      }
      return [...new Map(items.map(p=>[p.handle,p])).values()];
    })();
    const sourceFilterKey = key => {
      if (key.startsWith('filter.faith.')) return key.slice('filter.faith.'.length);
      if (!key.startsWith('filter.p.m.custom.')) return null;
      const nativeKey = key.slice('filter.p.m.custom.'.length);
      return ({featured_scripture_1:'scripture',price:'price',color_design:'color',biblical_theme:'theme',theme:'theme',included_jewelry:'includes',chain_style:'chain'})[nativeKey] || nativeKey;
    };
    const urlState = () => {
      const url = new URL(location.href), selected = {};
      url.searchParams.forEach((value,key)=>{const facetKey=sourceFilterKey(key);if(facetKey)(selected[facetKey] ||= []).push(value);});
      return {selected,sort:url.searchParams.get('sort_by')||'manual',page:Math.max(1,Number(url.searchParams.get('page'))||1)};
    };
    const formState = form => {
      const selected = {};
      form.querySelectorAll('[data-facet-key]:checked').forEach(input => (selected[input.dataset.facetKey] ||= []).push(input.value));
      return selected;
    };
    const updateControls = (items, selected) => {
      forms.forEach(form=>form.querySelectorAll('[data-facet-key]').forEach(input=>{
        const key=input.dataset.facetKey;
        input.checked=(selected[key]||[]).includes(input.value);
        const count=items.filter(p=>matchesFacets(p.facets,selected,key)&&(p.facets[key]||[]).includes(input.value)).length;
        const label=input.closest('label');
        const span=label.querySelector('[data-facet-count]');
        if(span) span.textContent=count;
        input.disabled=!count&&!input.checked;
      }));
      const count=selectProducts(items,selected).length;
      section.querySelectorAll('[data-mobile-result-count]').forEach(el=>el.textContent=count);
    };
    let sequence=0;
    const render = async ({selected,sort,page}, push=false) => {
      const current=++sequence;
      try {
        const items=await catalog();
        if (current!==sequence || !section.isConnected) return;
        const products=selectProducts(items,selected,sort),pages=Math.max(1,Math.ceil(products.length/perPage));
        page=Math.min(pages,page);
        grid.replaceChildren(...products.slice((page-1)*perPage,page*perPage).map(p=>p.card.cloneNode(true)));
        if (!products.length) {const empty=document.createElement('p');empty.className='faith-empty';empty.textContent='No products found.';grid.append(empty);}
        updateControls(items,selected);
        section.querySelector('[data-result-count]').textContent=products.length+' products';
        const url = new URL(location.href);
        [...url.searchParams.keys()].filter(k=>k.startsWith('filter.faith.')||k.startsWith('filter.p.m.custom.')||k==='page'||k==='sort_by').forEach(k=>url.searchParams.delete(k));
        for(const [key,values]of Object.entries(selected)) values.forEach(v=>url.searchParams.append('filter.faith.'+key,v));
        if(sort!=='manual')url.searchParams.set('sort_by',sort);
        if(page>1)url.searchParams.set('page',page);
        if(push)history.pushState({},'',url);
        forms.forEach(form=>form.querySelector('[name=sort_by]').value=sort);
        pagination.replaceChildren();
        const link=(number,label)=>{
          const a=document.createElement('a'),href=new URL(url);href.searchParams.set('page',number);a.href=href.pathname+href.search;a.textContent=label||String(number);a.setAttribute('aria-label','Go to page '+number);
          a.addEventListener('click',e=>{e.preventDefault();render({selected,sort,page:number},true);section.scrollIntoView({behavior:'smooth',block:'start'});});return a;
        };
        if(pages>1){
          if(page>1)pagination.append(link(page-1,'‹'));
          const shown=new Set([1,pages,page-1,page,page+1]);if(page===1){shown.add(2);shown.add(3);}
          let previous=0;
          [...shown].filter(n=>n>0&&n<=pages).sort((a,b)=>a-b).forEach(n=>{
            if(previous&&n>previous+1){const dots=document.createElement('span');dots.textContent='…';pagination.append(dots);}
            if(n===page){const span=document.createElement('span');span.textContent=n;span.setAttribute('aria-current','page');pagination.append(span);}else pagination.append(link(n));previous=n;
          });
          if(page<pages)pagination.append(link(page+1,'›'));
        }
        chips.replaceChildren();
        for(const [key,values]of Object.entries(selected)) values.forEach(value=>{
          const chip=document.createElement('button');chip.type='button';chip.textContent=value+' ×';chip.setAttribute('aria-label','Remove filter '+value);
          chip.addEventListener('click',()=>render({selected:{...selected,[key]:values.filter(v=>v!==value)},sort,page:1},true));chips.append(chip);
        });
        section.querySelectorAll('.faith-sort-options button').forEach(b=>{if(b.value===sort)b.setAttribute('aria-current','true');else b.removeAttribute('aria-current');});
        document.dispatchEvent(new CustomEvent('faith:catalog-rendered',{detail:{section}}));
        error.hidden=true;
      }catch(e){if(e.name!=='AbortError'){catalogPromise=undefined;error.textContent=e.message;error.hidden=false;}}
    };
    forms.forEach(form=>{
      form.addEventListener('change',async()=>{
        const selected=formState(form);
        if(form.dataset.facetMode==='desktop') render({selected,sort:urlState().sort,page:1},true);
        else {try{updateControls(await catalog(),selected);}catch(e){error.textContent=e.message;error.hidden=false;}}
      });
      form.addEventListener('submit',e=>{e.preventDefault();render({selected:formState(form),sort:urlState().sort,page:1},true);dialog?.close();});
    });
    section.querySelectorAll('[data-clear-facets]').forEach(a=>a.addEventListener('click',e=>{e.preventDefault();render({selected:{},sort:urlState().sort,page:1},true);}));
    section.querySelector('.faith-sort-options')?.addEventListener('submit',e=>{e.preventDefault();render({...urlState(),sort:e.submitter.value,page:1},true);section.querySelector('.faith-sort-popover').open=false;});
    window.addEventListener('popstate',()=>render(urlState()),{signal:controller.signal});
    render(urlState());
  });
}

if (typeof document !== 'undefined') {
  initializeCollections();
  document.addEventListener('shopify:section:load',e=>initializeCollections(e.target));
  document.addEventListener('shopify:section:unload',e=>e.target.querySelectorAll('[data-faith-collection]').forEach(el=>el.dispatchEvent(new Event('faith:dispose'))));
}
